import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { EuiFlyoutFooter, EuiButton, EuiFormRow, EuiFlyoutHeader, EuiTitle, EuiLoadingContent, EuiFlexGroup, EuiFlexItem, EuiText, EuiFieldText, EuiTextArea } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import {
  useGetMasterCampaignPlanQuery,
  useMasterCampaignPlanEditPlanDetailsMutation,
  useMasterCampaignPlanEditGeoDetailsMutation,
  useMasterCampaignPlanEditAudienceDetailsMutation,
  useMasterCampaignPlanEditSeasonalityDetailsMutation,
  useMasterCampaignPlanEditNameMutation
} from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import ConfigHeightEuiFlyoutBody from 'components/ConfigHeight/ConfigHeightEuiFlyoutBody'
import ConfigHeightEuiFormRow from 'components/ConfigHeight/ConfigHeightEuiFormRow'
import { EditSummaryForm } from 'features/planner/components/EditSummaryForm'

export enum EditType {
  Plan = 'Plan',
  Geo = 'Geo',
  Audience = 'Audience',
  Seasonality = 'Seasonality',
  Name = 'Name'
}

interface EditFlyoutContentProps {
  title: string
  form: React.ReactNode
}

interface MasterCampaignPlanPageParams {
  masterCampaignPlanId: string
}

export const EditFlyout = ({ editType, onClose }: { editType: EditType; onClose: () => void; selectedCampaignBuilderId?: string }) => {
  const { masterCampaignPlanId } = useParams<MasterCampaignPlanPageParams>()
  const { data: masterCampaignPlan, isFetching: isMasterCampaignPlanFetching } = useGetMasterCampaignPlanQuery({ masterCampaignPlanId: masterCampaignPlanId })

  const [name, setName] = useState(masterCampaignPlan?.name ?? '')

  const [planSummary, setPlanSummary] = useState(masterCampaignPlan?.planSummary ?? '')
  const [planDetails, setPlanDetails] = useState(masterCampaignPlan?.planDetails ?? '')

  const [geoSummary, setGeoSummary] = useState(masterCampaignPlan?.geoSummary ?? '')

  const [audienceSummary, setAudienceSummary] = useState(masterCampaignPlan?.audienceSummary ?? '')

  const [seasonalitySummary, setSeasonalitySummary] = useState(masterCampaignPlan?.seasonalitySummary ?? '')
  const [seasonalityDetails, setSeasonalityDetails] = useState(masterCampaignPlan?.seasonalityDetails ?? '')

  const [updatePlanDetailsMutation, { isLoading: isUpdatePlanDetailsLoading }] = useMasterCampaignPlanEditPlanDetailsMutation()
  const [updateGeoDetailsMutation, { isLoading: isUpdateGeoDetailsLoading }] = useMasterCampaignPlanEditGeoDetailsMutation()
  const [updateAudienceDetailsMutation, { isLoading: isUpdateAudienceDetailsLoading }] = useMasterCampaignPlanEditAudienceDetailsMutation()
  const [updateSeasonalityDetailsMutation, { isLoading: isUpdateSeasonalityDetailsLoading }] = useMasterCampaignPlanEditSeasonalityDetailsMutation()
  const [updateNameMutation, { isLoading: isUpdateNameLoading }] = useMasterCampaignPlanEditNameMutation()

  const isUpdating = isUpdatePlanDetailsLoading || isUpdateGeoDetailsLoading || isUpdateAudienceDetailsLoading || isUpdateSeasonalityDetailsLoading || isUpdateNameLoading
  const isEditNameSaveDisabled = name.length <= 0

  const onSave = () => {
    if (!masterCampaignPlan?.organizationId || !masterCampaignPlan?.accountId || !masterCampaignPlanId) {
      return
    }

    if (editType === EditType.Plan) {
      updatePlanDetailsMutation({ organizationId: masterCampaignPlan.organizationId, accountId: masterCampaignPlan?.accountId, masterCampaignPlanId: masterCampaignPlanId, planSummary: planSummary, planDetails: planDetails })
    }

    if (editType === EditType.Geo) {
      updateGeoDetailsMutation({ organizationId: masterCampaignPlan.organizationId, accountId: masterCampaignPlan?.accountId, masterCampaignPlanId: masterCampaignPlanId, geoSummary: geoSummary, geoDetails: masterCampaignPlan?.geoDetails })
    }

    if (editType === EditType.Audience) {
      updateAudienceDetailsMutation({ organizationId: masterCampaignPlan.organizationId, accountId: masterCampaignPlan?.accountId, masterCampaignPlanId: masterCampaignPlanId, audienceSummary: audienceSummary, audienceDetails: masterCampaignPlan?.audienceDetails })
    }

    if (editType === EditType.Seasonality) {
      updateSeasonalityDetailsMutation({ organizationId: masterCampaignPlan.organizationId, accountId: masterCampaignPlan?.accountId, masterCampaignPlanId: masterCampaignPlanId, seasonalitySummary: seasonalitySummary, seasonalityDetails: seasonalityDetails })
    }

    if (editType === EditType.Name) {
      updateNameMutation({ organizationId: masterCampaignPlan.organizationId, accountId: masterCampaignPlan?.accountId, masterCampaignPlanId: masterCampaignPlanId, name: name })
    }

    onClose()
  }

  const EditFlyoutContent = (): EditFlyoutContentProps => {
    switch (editType) {
      case EditType.Plan:
        return {
          title: 'Edit Plan Summary',
          form: <EditSummaryForm summaryLabel='Plan Summary' summaryPlaceholder='A few sentences explaining the plan strategy and intent.' summary={planSummary} detailsLabel='Plan Details' detailsPlaceholder='A longer description, including research, of the plan strategy and intent.' details={planDetails} onSummaryChange={setPlanSummary} onDetailsChange={setPlanDetails} />
        }
      case EditType.Geo:
        return {
          title: 'Edit Geographic Targeting',
          form: (
            <ConfigHeightEuiFormRow height='100%' fullWidth label='Geographic Targeting'>
              <EuiTextArea fullWidth style={{ height: '100%' }} placeholder='A summary of the geographic targeting.' value={geoSummary} onChange={e => setGeoSummary(e.target.value)} />
            </ConfigHeightEuiFormRow>
          )
        }
      case EditType.Audience:
        return {
          title: 'Edit Audience Strategy',
          form: (
            <ConfigHeightEuiFormRow height='100%' fullWidth label='Audience Strategy'>
              <EuiTextArea fullWidth style={{ height: '100%' }} placeholder='A summary of the audience strategy.' value={audienceSummary} onChange={e => setAudienceSummary(e.target.value)} />
            </ConfigHeightEuiFormRow>
          )
        }
      case EditType.Seasonality:
        return {
          title: 'Edit Seasonality',
          form: (
            <EditSummaryForm
              summaryLabel='Seasonality Summary'
              summaryPlaceholder='A few sentences explaining some of the seasonal events that may effect the plan strategy.'
              summary={seasonalitySummary}
              detailsLabel='Seasonality Details'
              detailsPlaceholder='A longer description, including research, explaining the strategy behind campaign launch times and seasonality.'
              details={seasonalityDetails}
              onSummaryChange={setSeasonalitySummary}
              onDetailsChange={setSeasonalityDetails}
            />
          )
        }
      case EditType.Name:
        return {
          title: 'Edit Name',
          form: (
            <EuiFormRow label='Master Campaign Plan Name' isInvalid={name.length === 0} error={'Name is required'}>
              <EuiFieldText value={name} onChange={e => setName(e.target.value)} />
            </EuiFormRow>
          )
        }
      default:
        return {
          title: '',
          form: <div></div>
        }
    }
  }

  return (
    <AcFlyout onClose={onClose}>
      <EuiFlyoutHeader>
        <EuiTitle size={'m'}>
          <h2>{EditFlyoutContent().title}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>
      <ConfigHeightEuiFlyoutBody height='100%'>{masterCampaignPlan?.id !== masterCampaignPlanId && isMasterCampaignPlanFetching ? <EuiLoadingContent /> : EditFlyoutContent().form}</ConfigHeightEuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup direction={'row'}>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onClose} isDisabled={isUpdating}>
              Cancel
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton fill onClick={onSave} isLoading={isUpdating} isDisabled={isEditNameSaveDisabled}>
              Save
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem />
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}
